// Slash commands the composer intercepts instead of sending to the agent.
const SLASH_COMMANDS = ["/clear"];

/**
 * Renders the text with @mentions highlighted behind a transparent textarea.
 */
export function BackdropContent({
  text,
  mentions,
}: {
  text: string;
  mentions: { label: string; startIndex: number }[];
}) {
  // A slash command only acts as one when it's the whole input; highlight it like
  // a mention (no padding/font-weight so backdrop metrics stay aligned).
  const command = SLASH_COMMANDS.find((c) => text.trim() === c);
  if (command) {
    const start = text.indexOf(command);
    return (
      <span className="whitespace-pre-wrap break-words text-slate-900 dark:text-gray-100">
        {text.slice(0, start)}
        <span className="rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">{command}</span>
        {text.slice(start + command.length)}
      </span>
    );
  }

  if (mentions.length === 0) {
    // No mentions — render the text normally to maintain layout
    return (
      <span className="whitespace-pre-wrap break-words text-slate-900 dark:text-gray-100">{text || "\u00A0"}</span>
    );
  }

  // Build segments using tracked startIndex positions for accurate rendering
  const sorted = [...mentions]
    .filter((m) => {
      const expected = `@${m.label}`;
      return text.slice(m.startIndex, m.startIndex + expected.length) === expected;
    })
    .sort((a, b) => a.startIndex - b.startIndex);

  const segments: { text: string; isMention: boolean }[] = [];
  let pos = 0;

  for (const m of sorted) {
    const displayText = `@${m.label}`;
    if (m.startIndex > pos) {
      segments.push({ text: text.slice(pos, m.startIndex), isMention: false });
    }
    segments.push({ text: displayText, isMention: true });
    pos = m.startIndex + displayText.length;
  }

  if (pos < text.length) {
    segments.push({ text: text.slice(pos), isMention: false });
  }

  return (
    <>
      {segments.map((seg, i) =>
        seg.isMention ? (
          <span key={i} className="rounded bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            {seg.text}
          </span>
        ) : (
          <span key={i} className="whitespace-pre-wrap text-slate-900 dark:text-gray-100">
            {seg.text}
          </span>
        ),
      )}
    </>
  );
}
